import { QuizConfig } from './types'
import { GB_EXIT_PATH } from '../components/constants'

const e5: QuizConfig = {
  productType: 'membership',
  screens: [
    // ============================================================
    // SECTION 1: Intro
    // ============================================================
    {
      path: '/e5/welcome-age-range',
      type: 'SelectScreen',
      hideProgressBar: true,
      props: {
        title: 'Transform your health with the <green>power of your gut</green>',
        description: 'Choose your age to start',
        questionKey: 'welcome_age_range',
        hideBackLink: true,
        directNavigation: true,
        columns: 2,
        showArrow: true,
        topImage: '/img/e4/table.jpg',
        topImageAspectRatio: '335 / 250',
        topImageAlt: 'People sharing a meal around a table',
        options: [
          { label: 'Age: 18-34', value: '18_34' },
          { label: 'Age: 35-49', value: '35_49' },
          { label: 'Age: 50-64', value: '50_64' },
          { label: 'Age: 65+', value: '65_plus' },
        ],
      },
    },
    {
      path: '/e5/social-proof',
      type: 'SocialProofScreen',
      hideProgressBar: true,
      props: {
        title: '3 million people',
        description: 'depend on ZOE each month to transform their health.',
        topImage: '/img/e4/social-proof.png',
        topImageAspectRatio: '335 / 286',
        topImageAlt: 'ZOE members',
      },
    },
    {
      path: '/e5/goals',
      type: 'MultiSelectScreen',
      props: {
        title: 'What do you want to achieve?',
        description: 'Choose as many as you want',
        questionKey: 'goals',
        options: [
          { label: 'Live longer', value: 'live_longer' },
          { label: 'Lose weight', value: 'lose_weight' },
          { label: 'Improve my gut health', value: 'improve_gut_health' },
          { label: 'Feel better', value: 'feel_better' },
          { label: 'Sleep better', value: 'sleep_better' },
        ],
      },
    },
    {
      path: '/e5/first-step',
      type: 'ContentScreen',
      hideProgressBar: true,
      props: {
        title: 'You\u2019ve taken the first step!',
        description: 'Now let\u2019s create your plan and make your goals a reality.',
        topImage: '/img/e4/first-step.jpg',
        topImageAlt: 'Person enjoying healthy food in a kitchen',
        topImageAspectRatio: '335 / 286',
      },
    },
    {
      path: '/e5/heard-about-gut-health',
      type: 'SelectScreen',
      props: {
        title: 'Have you heard about gut health',
        questionKey: 'heard_about_gut_health',
        options: [
          {
            label: 'Just the name',
            value: 'just_the_name',
            playback: "<b>Don't worry!</b> Together, we'll explore what truly drives your health.",
          },
          {
            label: 'I know a few things',
            value: 'a_few_things',
            playback: "<b>Nice start!</b> We'll build on your knowledge to transform your health.",
          },
          {
            label: 'I know everything about it!',
            value: 'everything',
            playback: "<b>Great stuff!</b> We'll dive deeper into the science to transform your health.",
          },
        ],
      },
    },
    {
      path: '/e5/zoe-science',
      type: 'MandatoryConsentScreen',
      hideProgressBar: true,
      props: {
        title: 'We\u2019re the world leaders in gut health',
        description: 'We personalise your plan with the world\u2019s largest microbiome DNA database.',
        topImage: '/img/e4/scientists.jpg',
        topImageAlt:
          'ZOE scientists in partnership with Kings College London, Stanford Medicine, and Massachusetts General Hospital',
        topImageAspectRatio: '335 / 250',
        consentLabel:
          'I agree to ZOE using my diet and health data to provide and personalise my quiz and onboarding experience.',
        consentQuestionKey: 'data_consent',
      },
    },

    // ============================================================
    // SECTION 2: Energy and sleep
    // ============================================================
    {
      path: '/e5/energy',
      type: 'SelectScreen',
      props: {
        title: 'How is your energy during the day?',
        questionKey: 'energy',
        options: [
          { label: 'Low, I feel tired all day', value: 'low' },
          { label: 'Inconsistent, I get slumps', value: 'inconsistent' },
          {
            label: 'High, I feel steady all day',
            value: 'high',
            playback: '<b>Amazing!</b> We\u2019ll help you fine-tune what\u2019s already working.',
          },
        ],
      },
    },
    {
      path: '/e5/energy-playback',
      type: 'ContentScreen',
      hideProgressBar: true,
      showWhen: { energy: ['low', 'inconsistent'] },
      props: {
        title: 'ZOE members report 4x more energy',
        description: 'We\u2019ll guide you to steady, lasting energy through better gut health.',
        topImage: '/img/e4/energy-graph.png',
        topImageAspectRatio: '335 / 286',
        imageObjectFit: 'contain',
      },
    },
    {
      path: '/e5/sleep-hours',
      type: 'SelectScreen',
      props: {
        title: 'How much sleep do you usually get?',
        questionKey: 'sleep_hours',
        options: [
          { label: 'Less than 5 hours', value: 'less_than_5' },
          { label: '5-6 hours', value: '5_6' },
          { label: '7-8 hours', value: '7_8' },
          { label: 'More than 8 hours', value: '8_plus' },
        ],
      },
    },
    {
      path: '/e5/sleep-quality',
      type: 'SelectScreen',
      props: {
        title: 'How is the quality of your sleep',
        questionKey: 'sleep_quality',
        options: [
          {
            label: 'Good, I sleep well',
            value: 'sleep_well',
            playback: '<b>Great stuff!</b> Your sleep is already supporting your gut.',
          },
          { label: 'I struggle falling asleep', value: 'struggle_falling_asleep' },
          { label: 'I wake up tired', value: 'wake_up_tired' },
          { label: 'I wake during the night', value: 'wake_during_night' },
          { label: 'I get hot flushes', value: 'hot_flushes' },
        ],
      },
    },
    {
      path: '/e5/sleep-playback',
      type: 'ContentScreen',
      hideProgressBar: true,
      showWhen: { sleep_quality: ['struggle_falling_asleep', 'wake_up_tired', 'wake_during_night', 'hot_flushes'] },
      props: {
        title: 'We\u2019re here to help',
        description: 'Poor sleep is frustrating and affects your health, but ZOE is scientifically proven to work.',
        topImage: '/img/e4/sleep-playback.png',
        topImageAspectRatio: '335 / 286',
      },
    },
    {
      path: '/e5/diet-type',
      type: 'SelectScreen',
      props: {
        title: 'Do you follow a specific diet?',
        questionKey: 'diet_type',
        options: [
          {
            label: 'No, I eat everything',
            value: 'no',
            playback: '<b>Your choices matter.</b> Our approach fits all dietary patterns.',
          },
          {
            label: 'Vegetarian',
            value: 'vegetarian',
            playback: '<b>Your choices matter.</b> Our approach fits all dietary patterns.',
          },
          {
            label: 'Vegan',
            value: 'vegan',
            playback: '<b>Your choices matter.</b> Our approach fits all dietary patterns.',
          },
          {
            label: 'Mediterranean-style',
            value: 'mediterranean_style',
            playback: '<b>Your choices matter.</b> Our approach fits all dietary patterns.',
          },
          {
            label: 'Gluten-free',
            value: 'gluten_free',
            playback: '<b>Your choices matter.</b> Our approach fits all dietary patterns.',
          },
          {
            label: 'Dairy-free',
            value: 'dairy_free',
            playback: '<b>Your choices matter.</b> Our approach fits all dietary patterns.',
          },
          {
            label: 'Other',
            value: 'other',
            playback: '<b>Your choices matter.</b> Our approach fits all dietary patterns.',
          },
        ],
      },
    },

    // ============================================================
    // SECTION 3: Diet quality
    // ============================================================
    {
      path: '/e5/diet-quality',
      type: 'SelectScreen',
      props: {
        title: 'How would you rate your diet quality?',
        questionKey: 'diet_quality',
        options: [
          {
            label: 'It\u2019s not as healthy as I\u2019d like',
            value: 'poor',
            playback: '<b>You\u2019re not alone.</b> 52% of ZOE members start right here.',
          },
          {
            label: 'It varies day-to-day',
            value: 'average',
            playback: '<b>You\u2019re already doing a lot of things well!</b> We\u2019ll make consistency easy.',
          },
          {
            label: 'It\u2019s healthy and well-balanced',
            value: 'good',
            playback: '<b>That\u2019s great to hear!</b> We\u2019ll build on your strong foundations.',
          },
        ],
      },
    },
    {
      path: '/e5/snacker',
      type: 'SelectScreen',
      props: {
        title: 'Are you a snacker?',
        questionKey: 'snacker',
        options: [
          {
            label: 'Yes',
            value: 'yes',
            playback: '<b>Snack quality impacts your health.</b> Our landmark 2023 study proves it.',
          },
          {
            label: 'No',
            value: 'no',
            playback: '<b>Not a snacker?</b> We\u2019ll help you build balanced meals for lasting fullness.',
          },
        ],
      },
    },
    {
      path: '/e5/processed-food-frequency',
      type: 'SelectScreen',
      props: {
        title: 'How often do you eat processed foods?',
        description: 'Examples include shop-bought bread, sweeteners, fizzy drinks, ready meals, and breakfast cereals',
        questionKey: 'processed_food_frequency',
        options: [
          {
            label: 'Most meals',
            value: 'most_meals',
            playback: '<b>They\u2019re hard to avoid!</b> Our first-of-its-kind scale will show you what to swap.',
          },
          {
            label: 'Once a day',
            value: 'once_a_day',
            playback: '<b>They\u2019re hard to avoid!</b> Our first-of-its-kind scale will show you what to swap.',
          },
          {
            label: 'A few times a week',
            value: 'few_times_a_week',
            playback: '<b>They\u2019re hard to avoid!</b> Our first-of-its-kind scale will show you what to swap.',
          },
          {
            label: 'Rarely or never',
            value: 'rarely_never',
            playback: '<b>Nice one!</b> We\u2019ll help you keep up your good habits.',
          },
        ],
      },
    },
    {
      path: '/e5/plant-frequency',
      type: 'SelectScreen',
      props: {
        title: 'How many plants do you eat a day?',
        description: 'Examples include fruits, vegetables, whole grains, nuts, seeds, herbs, and spices',
        questionKey: 'plant_frequency',
        options: [
          {
            label: '1-3 plants',
            value: '1_3',
            playback: '<b>Let\u2019s aim higher!</b> Our science confirms more plants are the key to gut health.',
          },
          {
            label: '4-6 plants',
            value: '4_6',
            playback: '<b>Almost there!</b> Our science shows more plants unlock great gut health.',
          },
          {
            label: '7+ plants',
            value: '7_plus',
            playback: '<b>You\u2019ve got the key to a great gut!</b> Now, let our science optimise your plant mix.',
          },
        ],
      },
    },
    {
      path: '/e5/thanks-for-sharing',
      type: 'ContentScreen',
      hideProgressBar: true,
      props: {
        title: 'Thanks for sharing!',
        description: 'Your gut is unique. With our science, your diet will be too.',
        topImage: '/img/e4/thanks-for-sharing.png',
        topImageAspectRatio: '335 / 286',
      },
    },

    // ============================================================
    // SECTION 4a: About you (no weight loss)
    // ============================================================
    {
      path: '/e5/lifestyle-activity',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'How active is your daily routine?',
        questionKey: 'lifestyle_activity',
        options: [
          {
            label: 'Not active \u2013 I have limited mobility or cannot exercise',
            value: 'not_active',
            playback: '<b>No problem!</b> We\u2019ll help you optimise your health through the power of food.',
          },
          {
            label: 'Mostly sedentary \u2013 I sit for most of the day',
            value: 'mostly_sedentary',
            playback: '<b>We\u2019ve got you covered!</b> We\u2019ll help you support your health with every meal.',
          },
          {
            label: 'Lightly active \u2013 I move often',
            value: 'lightly_active',
            playback: '<b>Nice!</b> We\u2019ll make sure you have the perfect fuel for your day.',
          },
          {
            label: 'Very active \u2013 I have a physical job or exercise regularly',
            value: 'very_active',
            playback: '<b>Great!</b> We\u2019ll give you science-backed nutrition advice to fuel your activities.',
          },
        ],
      },
    },
    {
      path: '/e5/biological-sex',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'What sex were you assigned at birth?',
        description: 'Biological sex affects gut microbiome composition',
        questionKey: 'biological_sex',
        options: [
          { label: 'Male', value: 'male' },
          { label: 'Female', value: 'female' },
        ],
      },
    },
    {
      path: '/e5/height',
      type: 'InputHeightScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'What\u2019s your height?',
        subtitle: 'Your height influences your metabolism and helps shape our recommendations.',
        defaultUnit: 'ft',
      },
    },
    {
      path: '/e5/current-weight',
      type: 'InputWeightScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      useAltProps: { quiz_country: 'US' },
      props: {
        title: 'What\u2019s your current weight?',
        description: 'We use this to tailor your plan by understanding your body\u2019s unique metabolism.',
        defaultWeightUnit: 'kg',
      },
      altProps: {
        title: 'What\u2019s your current weight?',
        description: 'We use this to tailor your plan by understanding your body\u2019s unique metabolism.',
        defaultWeightUnit: 'kg',
      },
    },
    {
      path: '/e5/glp1s',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'Are you currently taking weight loss medication?',
        description: 'Examples include Wegovy, Mounjaro, and Saxenda',
        questionKey: 'glp1s',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback: '<b>Noted!</b> We\u2019ll help you with lifestyle changes that drive lasting success.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },
    {
      path: '/e5/antibiotics',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'Have you taken antibiotics in the last three years?',
        questionKey: 'antibiotics',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>Antibiotics save lives, but they also kill good gut bacteria.</b> The good news? With our plan, you can improve your gut health in just weeks.',
          },
          {
            label: 'No',
            value: 'false',
            playback:
              '<b>Great!</b> Antibiotics can harm your gut and kill good gut bacteria, so you\u2019re off to a great start.',
          },
        ],
      },
    },
    {
      path: '/e5/blood-tests',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'Have you had any blood tests in the last two years?',
        questionKey: 'blood_tests',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>Great!</b> Once you start your plan, share your results to unlock more tailored insights into your blood sugar and fat responses to food.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },

    // ============================================================
    // SECTION 4b: About you (weight loss)
    // ============================================================
    {
      path: '/e5/wl-biological-sex',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'What sex were you assigned at birth?',
        description: 'Biological sex affects gut microbiome composition',
        questionKey: 'biological_sex',
        options: [
          { label: 'Male', value: 'male' },
          { label: 'Female', value: 'female' },
        ],
      },
    },
    {
      path: '/e5/wl-height',
      type: 'InputHeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'What\u2019s your height?',
        subtitle: 'Your height influences your metabolism and helps shape our recommendations.',
        defaultUnit: 'ft',
      },
    },
    {
      path: '/e5/wl-current-weight',
      type: 'InputWeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      useAltProps: { quiz_country: 'US' },
      props: {
        title: 'What\u2019s your current weight?',
        description: 'We use this to tailor your plan by understanding your body\u2019s unique metabolism.',
        defaultWeightUnit: 'kg',
      },
      altProps: {
        title: 'What\u2019s your current weight?',
        description: 'We use this to tailor your plan by understanding your body\u2019s unique metabolism.',
        defaultWeightUnit: 'kg',
      },
    },
    {
      path: '/e5/future-weight',
      type: 'InputTargetWeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'And what weight would you like to be?',
        descriptionLbs: 'Weight loss can come naturally as you transform your gut health.',
        descriptionKg: 'Weight loss can come naturally as you transform your gut health.',
      },
    },
    {
      path: '/e5/weight-proven',
      type: 'ContentScreen',
      hideProgressBar: true,
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Better gut health leads to lasting weight loss',
        description:
          'In our clinical trial, those who followed ZOE advice reduced their waistline by over 2 inches and lost over 4 kg, all without counting calories or restricting foods.',
        topImage: '/img/e4/weight-proven.png',
        topImageAspectRatio: '335 / 250',
        titleAboveImage: true,
        imageObjectFit: 'contain',
        footerBody: 'Source: ZOE research in Nature Medicine, 2024',
      },
    },
    {
      path: '/e5/wl-lifestyle-activity',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'How active is your daily routine?',
        questionKey: 'lifestyle_activity',
        options: [
          {
            label: 'Not active \u2013 I have limited mobility or cannot exercise',
            value: 'not_active',
            playback: '<b>No problem!</b> We\u2019ll help you optimise your health through the power of food.',
          },
          {
            label: 'Mostly sedentary \u2013 I sit for most of the day',
            value: 'mostly_sedentary',
            playback: '<b>We\u2019ve got you covered!</b> We\u2019ll help you support your health with every meal.',
          },
          {
            label: 'Lightly active \u2013 I move often',
            value: 'lightly_active',
            playback: '<b>Nice!</b> We\u2019ll make sure you have the perfect fuel for your day.',
          },
          {
            label: 'Very active \u2013 I have a physical job or exercise regularly',
            value: 'very_active',
            playback: '<b>Great!</b> We\u2019ll give you science-backed nutrition advice to fuel your activities.',
          },
        ],
      },
    },
    {
      path: '/e5/wl-glp1s',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Are you currently taking GLP-1 weight loss medication?',
        description: 'Examples include Wegovy, Mounjaro, and Saxenda',
        questionKey: 'glp1s',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>ZOE is the perfect companion for your weight loss journey.</b> We\u2019ll help you make lasting health improvements, choose smarter foods, and give your body the nutrients it needs to thrive.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },
    {
      path: '/e5/wl-antibiotics',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Have you taken antibiotics in the last three years?',
        questionKey: 'antibiotics',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>Antibiotics save lives, but they also kill good gut bacteria.</b> The good news? With our plan, you can improve your gut health in just weeks.',
          },
          {
            label: 'No',
            value: 'false',
            playback:
              '<b>Great!</b> Antibiotics can harm your gut and kill good gut bacteria, so you\u2019re off to a great start.',
          },
        ],
      },
    },
    {
      path: '/e5/wl-blood-tests',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Have you had any blood tests in the last two years?',
        questionKey: 'blood_tests',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>Great!</b> Once you start your plan, share your results to unlock more tailored insights into your blood sugar and fat responses to food.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },
    {
      path: '/e5/goal-graph',
      type: 'WeightCalculationScreen',
      hideProgressBar: true,
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Transform your gut health and lose weight with ZOE',
        description:
          'Based on your answers, we predict you\u2019ll lose weight with ZOE, <b>sleep better, have more energy, live longer,</b> and much more.',
      },
    },
    {
      path: '/e5/zoe-is-different-1',
      type: 'ContentScreen',
      hideProgressBar: true,
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'You\u2019ll get personalised advice based on your unique biology.',
        description: 'Why ZOE is different',
        topImage: '/img/e4/zoe-different-1.jpg',
        topImageAspectRatio: '335 / 286',
        subtitleAboveTitle: true,
        centerText: true,
      },
    },
    {
      path: '/e5/zoe-is-different-2',
      type: 'ContentScreen',
      hideProgressBar: true,
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Your plan will be built with data from the world\u2019s largest gut health study.',
        description: 'Why ZOE is different',
        topImage: '/img/e4/zoe-different-2.png',
        topImageAspectRatio: '335 / 286',
        subtitleAboveTitle: true,
        centerText: true,
      },
    },
    {
      path: '/e5/zoe-is-different-3',
      type: 'ContentScreen',
      hideProgressBar: true,
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'You can still eat the foods you love. No restriction and no calorie counting.',
        description: 'Why ZOE is different',
        topImage: '/img/e4/zoe-different-3.jpg',
        topImageAspectRatio: '335 / 286',
        subtitleAboveTitle: true,
        centerText: true,
      },
    },
    {
      path: '/e5/zoe-is-different-4',
      type: 'ContentScreen',
      hideProgressBar: true,
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'You\u2019ll transform every aspect of your health, from mood and sleep to weight and longevity.',
        description: 'Why ZOE is different',
        topImage: '/img/e4/zoe-different-4.jpg',
        topImageAspectRatio: '335 / 286',
        subtitleAboveTitle: true,
        centerText: true,
      },
    },

    // ============================================================
    // SECTION 4a/4b CONVERGENCE: Health Summary
    // ============================================================
    {
      path: '/e5/health-summary',
      type: 'HealthSummaryScreen',
      hideProgressBar: true,
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      props: {
        title: 'Here\u2019s your health summary',
        summaryItems: [
          {
            label: 'Sleep duration',
            questionKey: 'sleep_hours',
            icon: 'moon',
            formatAnswer: {
              less_than_5: 'Less than 5 hours',
              '5_6': '5-6 hours',
              '7_8': '7-8 hours',
              '8_plus': 'More than 8 hours',
            },
          },
          {
            label: 'Sleep quality',
            questionKey: 'sleep_quality',
            icon: 'cgm',
            formatAnswer: {
              sleep_well: 'Good',
              struggle_falling_asleep: 'Average',
              wake_up_tired: 'Average',
              wake_during_night: 'Poor',
              hot_flushes: 'Poor',
            },
          },
          {
            label: 'Energy',
            questionKey: 'energy',
            icon: 'flash',
            formatAnswer: {
              low: 'Low',
              inconsistent: 'Inconsistent',
              high: 'High',
            },
          },
          {
            label: 'Lifestyle',
            questionKey: 'lifestyle_activity',
            icon: 'body-fat',
            formatAnswer: {
              not_active: 'Not active',
              mostly_sedentary: 'Sedentary',
              lightly_active: 'Lightly active',
              very_active: 'Very active',
            },
          },
          {
            label: 'Diet type',
            questionKey: 'diet_type',
            icon: 'food-drink',
            formatAnswer: {
              no: 'Omnivore',
              vegetarian: 'Vegetarian',
              vegan: 'Vegan',
              mediterranean_style: 'Mediterranean-style',
              gluten_free: 'Gluten-free',
              dairy_free: 'Dairy-free',
              other: 'Other',
            },
          },
          {
            label: 'Diet quality',
            questionKey: 'diet_quality',
            icon: 'gut-health-test',
            formatAnswer: {
              poor: 'Low',
              average: 'Average',
              good: 'High',
            },
          },
          {
            label: 'Current weight',
            questionKey: 'current_weight',
            icon: 'scales',
            formatFn: 'weightWithUnit' as const,
            showWhen: { has_weight_loss_goal: 'true' },
          },
          {
            label: 'Ideal weight',
            questionKey: 'ideal_weight',
            icon: 'calendar-2',
            formatFn: 'weightWithUnit' as const,
            showWhen: { has_weight_loss_goal: 'true' },
          },
        ],
      },
    },

    // ============================================================
    // SECTION 5: Gut health quiz (convergence — all users)
    // ============================================================
    {
      path: '/e5/motivation',
      type: 'SelectScreen',
      props: {
        title: 'What\u2019s your main motivation?',
        questionKey: 'motivation',
        options: [
          {
            label: 'Getting healthier',
            value: 'getting_healthier',
            playback:
              '<b>Focusing on health is powerful!</b> Our clinical trial proves that following our advice leads to significant health improvements.',
          },
          {
            label: 'Feeling happier',
            value: 'feeling_happier',
            playback:
              "<b>Your gut and brain are deeply linked.</b> We'll help you feed your gut and boost the chemicals that support your mood.",
          },
          {
            label: 'A recent diagnosis',
            value: 'recent_diagnosis',
            playback: "<b>We're here to help.</b> With ZOE, you'll feel empowered to take control of your health.",
          },
          {
            label: 'Changing how I look',
            value: 'looking_different',
            playback: "<b>Self-love is a strong motivator!</b> We'll support you in reaching your goal.",
          },
          {
            label: 'Something else',
            value: 'other',
            playback: "<b>Every \u201Cwhy\u201D is valid!</b> Whatever yours is, we're here to help you achieve it.",
          },
        ],
      },
    },
    {
      path: '/e5/gut-quiz-intro',
      type: 'ContentScreen',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      props: {
        title: 'At ZOE, we know your gut is the key to transforming your health.',
        description:
          'Now, we want to understand where you are on your gut health journey, so we can better personalise your plan.',
        topImage: '/img/e4/gut-illustration.jpg',
        topImageAspectRatio: '335 / 250',
      },
    },
    {
      path: '/e5/gut-dna-question',
      type: 'SelectScreen',
      props: {
        title: 'Your gut is more unique than your DNA',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_dna_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e5/gut-dna-answer',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_dna_question',
        correctAnswer: 'true',
        correctIcon: '/img/shared/thumbs-up.svg',
        incorrectIcon: '/img/shared/not-quite.svg',
        description:
          'The gut houses <b>trillions of bacteria, all completely unique to you.</b> Even identical twins share just 34% of the same gut bacteria!',
        footer: 'Source: ZOE research in Nature Medicine, 2020',
      },
    },
    {
      path: '/e5/gut-impacts-health-question',
      type: 'SelectScreen',
      props: {
        title: 'Your gut impacts almost every aspect of your health',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_impacts_health_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e5/gut-impacts-health-answer',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_impacts_health_question',
        correctAnswer: 'true',
        correctIcon: '/img/shared/thumbs-up.svg',
        incorrectIcon: '/img/shared/not-quite.svg',
        description:
          'Our understanding of gut microbes has transformed in the last decade. We now know that <b>your gut is the foundation of your health.</b>{br}{br}' +
          'Beyond digestion, it regulates metabolism, influences mood, and houses 70% of your immune system.',
        footer: 'Source: Hou et al., 2022 and Wiertsema et al., 2021',
      },
    },
    {
      path: '/e5/gut-change-in-days-question',
      type: 'SelectScreen',
      props: {
        title: 'It takes at least 3 months to change your gut microbiome',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_change_in_days_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e5/gut-change-in-days-answer',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_change_in_days_question',
        correctAnswer: 'false',
        correctIcon: '/img/shared/thumbs-up.svg',
        incorrectIcon: '/img/shared/not-quite.svg',
        description:
          '<b>Your gut microbiome starts to change in just days</b>, and these changes will steadily build over the following months with ZOE.{br}{br}' +
          'By choosing foods that suit your unique biology, you\u2019ll harness the power of your gut to reach your health goals.',
        footer: 'Source: Wu et al., 2012',
      },
    },

    // ============================================================
    // SECTION 5b: Symptom questionnaire (e5 addition)
    // ============================================================
    {
      path: '/e5/symptom-intro',
      type: 'ContentScreen',
      hideProgressBar: true,
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      props: {
        title: 'Get a better picture of your health',
        description:
          'Finally, we\u2019d like to learn more about the symptoms you\u2019re experiencing.{br}{br}' +
          'We\u2019ll use your answers to provide a unique score and show you how much better you could feel with ZOE.',
        topImage: '/img/e5/symptom-intro-collage.png',
        imageObjectFit: 'contain',
        tightTop: true,
      },
    },
    {
      path: '/e5/symptom-gut-and-appetite',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['4', '3', '2', '1'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Gut and appetite', colorScheme: 'gut' },
        questions: [
          { question: 'Digestive symptoms (e.g. bloating, gas, discomfort)', questionKey: 'digestive_symptoms' },
          { question: 'Irregular bowel movements', questionKey: 'irregular_bowel_movements' },
          { question: 'Hunger', questionKey: 'hunger' },
          { question: 'Cravings', questionKey: 'cravings' },
        ],
      },
    },
    {
      path: '/e5/symptom-physical',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['4', '3', '2', '1'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Physical', colorScheme: 'physical' },
        questions: [
          { question: 'Joint and muscular discomfort', questionKey: 'joint_and_muscular_discomfort' },
          {
            question: 'Decreased physical strength or stamina',
            questionKey: 'decreased_physical_strength_or_stamina',
          },
          { question: 'Declining skin quality', questionKey: 'declining_skin_quality' },
          { question: 'Declining hair quality', questionKey: 'declining_hair_quality' },
        ],
      },
    },
    {
      path: '/e5/symptom-mood',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['4', '3', '2', '1'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Mood', colorScheme: 'mood' },
        questions: [
          { question: 'Irritability', questionKey: 'irritability' },
          { question: 'Low mood or mood swings', questionKey: 'low_mood_or_mood_swings' },
          { question: 'Feeling stressed or on edge', questionKey: 'feeling_stressed_or_on_edge' },
        ],
      },
    },
    {
      path: '/e5/symptom-energy',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['4', '3', '2', '1'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Energy', colorScheme: 'energy' },
        questions: [
          { question: 'Sleep problems', questionKey: 'sleep_problems' },
          { question: 'Tiredness and fatigue', questionKey: 'tiredness_and_fatigue' },
          { question: 'Brain fog', questionKey: 'brain_fog' },
        ],
      },
    },
    {
      path: '/e5/symptom-score-calculating',
      type: 'SymptomScoreCalculatingScreen',
      hideProgressBar: true,
      props: {},
    },
    {
      path: '/e5/symptom-score',
      type: 'SymptomScoreResultsScreen',
      hideProgressBar: true,
      props: {},
    },

    // ============================================================
    // SECTION 6a: Future you \u2014 no weight loss (symptom score graph)
    // ============================================================
    {
      path: '/e5/future-score',
      type: 'FutureScoreScreen',
      hideProgressBar: true,
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        variants: {
          '0-89': {
            title: 'Feel better in weeks with ZOE',
            description:
              'ZOE has helped members just like you use real food to feel better and improve their health for good.{br}{br}' +
              'You\u2019ll also <b>boost your energy, support long-term health</b> and much more.',
          },
          '90-97': {
            title: 'Optimise your health with ZOE',
            description:
              'ZOE has helped members just like you use real food to feel better and optimise their health for good.{br}{br}' +
              'You\u2019ll also <b>boost your energy, support long-term health</b> and much more.',
          },
          '99-100': {
            title: 'Optimise your health with ZOE',
            description:
              'ZOE has helped members just like you use real food to feel better and optimise their health for good.{br}{br}' +
              'You\u2019ll also <b>boost your energy, support your long-term health</b> and much more.',
            image: '/img/e4/zoe-different-4.jpg',
          },
        },
      },
    },

    // ============================================================
    // SECTION 6b: Future you \u2014 weight loss (symptom score graph)
    // ============================================================
    {
      path: '/e5/future-score-weight-loss',
      type: 'FutureScoreScreen',
      hideProgressBar: true,
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        variants: {
          '0-89': {
            title: 'Feel better in weeks with ZOE',
            description:
              'ZOE has helped members just like you use real food to feel better and improve their health for good.{br}{br}' +
              'You\u2019ll also <b>lose weight while improving your energy, long-term health</b> and more.',
          },
          '90-97': {
            title: 'Optimise your health with ZOE',
            description:
              'ZOE has helped members just like you use real food to feel better and optimise their health for good.{br}{br}' +
              'You\u2019ll also <b>lose weight while improving your energy, long-term health</b> and more.',
          },
          '99-100': {
            title: 'Optimise your health with ZOE',
            description:
              'ZOE has helped members just like you use real food to feel better and optimise their health for good.{br}{br}' +
              'You\u2019ll also <b>lose weight while improving your energy, long-term health</b> and more.',
            image: '/img/e5/future-score-family.jpg',
          },
        },
      },
    },

    // ============================================================
    // SECTION 7: Wrap-up (convergence — all users)
    // ============================================================
    {
      path: '/e5/email',
      type: 'EmailCaptureScreen',
      props: {
        title: 'Get your personalised plan and free gut health guide',
        topImage: '/img/e4/email-guide.png',
        topImageAspectRatio: '1005 / 471',
        buttonText: 'Continue',
        footerBody:
          "We'll also send you ongoing science and nutrition emails, news and offers.{br}{optOutLink}. See our {privacyPolicy}.",
        showMarketingConsent: true,
        defaultMarketingOptIn: true,
      },
    },
    {
      path: '/e5/results',
      type: 'ResultsLoadingScreen',
      hideProgressBar: true,
      props: {
        title: 'We are building your personalised plan',
        steps: [
          'Analysing your health data',
          'Reviewing your goals',
          'Personalising your insights',
          'Generating your action plan',
        ],
        trustedByText: 'Trusted by over 3 million',
        testimonials: [
          {
            name: 'Alison',
            quote:
              'I\u2019ve noticed a change in my digestion, and I\u2019ve noticed a change in my weight. And that\u2019s all without restricting anything.',
          },
          {
            name: 'Selena',
            quote:
              'I feel more confident about food, food choices and regaining my independence, and I am seeing the difference in my energy.',
          },
          {
            name: 'Patty',
            quote:
              'Admittedly, I have been a sugar addict. I have noticed this week that I am no longer desiring highly processed, high sugar candy!',
          },
          {
            name: 'Kate',
            quote: 'Changed my mindset to focus on whole foods and not calorie content.',
          },
          {
            name: 'Dorna',
            quote:
              'Before Zoe, I think I was feeling lethargic. And now I feel a lot healthier and I\u2019m more energetic. And I have the energy to exercise more go out on longer hikes.',
          },
        ],
        buttonText: 'Discover my plan',
        exitPath: GB_EXIT_PATH,
      },
    },
  ],
}

export default e5
